import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, auth, BACKEND_URL } from '../../firebase/config';
import { collection, getDocs, deleteDoc, doc, updateDoc, setDoc, getDoc } from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { Users, Database, Shield, LogOut, Layout, Trash2, Settings, Download, FileText, UserCog, Edit, Save, X, Search, Filter, RefreshCw, Calendar, Activity, Plus, Eye, EyeOff, CheckCircle2, AlertTriangle, Home, HardDrive, Server, Wrench, ArrowRight, Upload, Code2, Gauge, Clock3 } from 'lucide-react';
import WidgetMaker from '../widgets/WidgetMaker';
import DashboardConfigurator from './DashboardConfigurator';

const DEV_BACKUP_HISTORY_KEY = 'ybit-developer-backup-history';
const DRIVE_ROOT_URL = `${BACKEND_URL.replace(/\/$/, '')}/api/upload/test`;
const DB_URL = `${BACKEND_URL.replace(/\/$/, '')}/api/db`;

const CONTROL_COLLECTIONS = [
  'users',
  'students',
  'dashboardConfigs',
  'customWidgets',
  'facultyAssignments',
  'divisionAssignments',
  'batchAssignments',
  'semesterSubjects',
  'attendance',
  'courseAttendance',
  'courseMarks',
  'iaTest1',
  'iaTest2',
  'parentMeetRecords',
  'achievements',
  'leaves',
  'studentOffices',
  'messages',
  'notifications',
  'systemConfig'
];

const getSessionToken = () => {
  const sessionKeys = ['adminSession', 'userSession', 'studentSession'];

  for (const key of sessionKeys) {
    const rawSession = sessionStorage.getItem(key);
    if (!rawSession) continue;

    try {
      const token = JSON.parse(rawSession)?.sessionToken || '';
      if (token) return token;
    } catch (error) {
      console.warn(`Could not parse ${key}.`, error);
    }
  }

  return '';
};

const readBackupHistory = () => {
  try {
    return JSON.parse(localStorage.getItem(DEV_BACKUP_HISTORY_KEY) || '[]');
  } catch (error) {
    console.warn('Could not read backup history.', error);
    return [];
  }
};

const writeBackupHistory = (backup) => {
  const history = [backup, ...readBackupHistory()].slice(0, 6);
  localStorage.setItem(DEV_BACKUP_HISTORY_KEY, JSON.stringify(history));
  return history;
};

const downloadJson = (filename, payload) => {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const countRecords = (data = {}) => Object.values(data).reduce((total, collectionValue) => {
  if (Array.isArray(collectionValue)) return total + collectionValue.length;
  if (collectionValue && typeof collectionValue === 'object') return total + Object.keys(collectionValue).length;
  return total;
}, 0);

const formatDateTime = (value) => {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Never';
  return date.toLocaleString();
};

const safeFilePart = (value) => String(value || 'backup').replace(/[^a-z0-9_-]+/gi, '-').replace(/^-+|-+$/g, '') || 'backup';

const fetchDriveDatabase = async () => {
  const token = getSessionToken();
  if (!token) throw new Error('Developer session token is missing. Log in again.');

  const response = await fetch(DB_URL, {
    headers: { Authorization: `Bearer ${token}` }
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok || !result.success) {
    throw new Error(result.error || 'Could not read the Google Drive database.');
  }

  return result.data || {};
};

const writeDriveDatabase = async (data) => {
  const token = getSessionToken();
  if (!token) throw new Error('Developer session token is missing. Log in again.');

  const response = await fetch(DB_URL, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({ data })
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok || !result.success) {
    throw new Error(result.error || 'Could not update the Google Drive database.');
  }

  return result;
};

const exportFirestoreCollections = async () => {
  const entries = await Promise.all(CONTROL_COLLECTIONS.map(async (collectionName) => {
    const snap = await getDocs(collection(db, collectionName));
    return [collectionName, snap.docs.map(snapshot => ({ id: snapshot.id, ...snapshot.data() }))];
  }));

  return Object.fromEntries(entries);
};

const buildControlCenterBackup = async () => {
  try {
    return {
      mode: 'google-drive',
      warning: '',
      data: await fetchDriveDatabase()
    };
  } catch (driveError) {
    console.warn('Drive export failed, falling back to collection export.', driveError);
    return {
      mode: 'collection-export',
      warning: driveError.message || 'Drive export was not available.',
      data: await exportFirestoreCollections()
    };
  }
};

const restoreCollectionExport = async (data) => {
  for (const [collectionName, documents] of Object.entries(data || {})) {
    if (!Array.isArray(documents)) continue;

    for (const item of documents) {
      const { id, ...documentData } = item || {};
      if (id) {
        await setDoc(doc(db, collectionName, id), documentData);
      }
    }
  }
};

// Staff Management Component
const ManageUsers = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [editingStaff, setEditingStaff] = useState(null);
  const [addingStaff, setAddingStaff] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [newStaff, setNewStaff] = useState({
    name: '',
    designation: '',
    department: 'Engineering Sciences & Humanities',
    qualifications: '',
    phone: '',
    email: '',
    username: '',
    password: '',
    role: 'mentor'
  });

  useEffect(() => {
    loadStaff();
  }, []);

  const loadStaff = async () => {
    try {
      setLoading(true);
      const usersSnap = await getDocs(collection(db, 'users'));
      const staffData = usersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setStaff(staffData);
      setLoading(false);
    } catch (error) {
      console.error('Error loading staff:', error);
      setLoading(false);
    }
  };

  const handleDeleteStaff = async (staffId, staffName) => {
    if (!window.confirm(`Delete staff member "${staffName}"?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'users', staffId));
      setStaff(prev => prev.filter(s => s.id !== staffId));
      window.alert('✅ Staff member deleted successfully!');
    } catch (error) {
      console.error('Error deleting staff:', error);
      window.alert('❌ Error deleting staff: ' + error.message);
    }
  };

  const handleEditStaff = (staffMember) => {
    setEditingStaff({ ...staffMember, password: '' });
    setShowPassword(false);
  };

  const handleSaveStaff = async () => {
    try {
      const staffRef = doc(db, 'users', editingStaff.id);
      
      const updateData = {
        name: editingStaff.name,
        designation: editingStaff.designation,
        department: editingStaff.department,
        qualifications: editingStaff.qualifications,
        phone: editingStaff.phone,
        email: editingStaff.email,
        username: editingStaff.username,
        role: editingStaff.role
      };

      // If password is provided, include it
      if (editingStaff.password && editingStaff.password.trim() !== '') {
        updateData.password = editingStaff.password;
      }
      
      await updateDoc(staffRef, updateData);
      
      setStaff(prev => prev.map(s => s.id === editingStaff.id ? { ...s, ...updateData } : s));
      setEditingStaff(null);
      window.alert('✅ Staff member updated successfully!');
    } catch (error) {
      console.error('Error updating staff:', error);
      window.alert('❌ Error updating staff: ' + error.message);
    }
  };

  const handleAddStaff = async () => {
    try {
      // Validate required fields
      if (!newStaff.name || !newStaff.email || !newStaff.password) {
        window.alert('❌ Please fill in Name, Email, and Password');
        return;
      }

      // Create user in Firebase Auth
      try {
        await createUserWithEmailAndPassword(auth, newStaff.email, newStaff.password);
      } catch (authError) {
        console.error('Auth error:', authError);
        // Continue anyway - user might already exist
      }

      // Add to Firestore
      const staffData = {
        name: newStaff.name,
        designation: newStaff.designation,
        department: newStaff.department,
        qualifications: newStaff.qualifications,
        phone: newStaff.phone,
        email: newStaff.email,
        username: newStaff.username,
        password: newStaff.password,
        role: newStaff.role,
        createdAt: new Date().toISOString()
      };

      await setDoc(doc(db, 'users', newStaff.email), staffData);
      
      setStaff(prev => [...prev, { id: newStaff.email, ...staffData }]);
      setAddingStaff(false);
      setNewStaff({
        name: '',
        designation: '',
        department: 'Engineering Sciences & Humanities',
        qualifications: '',
        phone: '',
        email: '',
        username: '',
        password: '',
        role: 'mentor'
      });
      window.alert('✅ Staff member added successfully!');
      loadStaff();
    } catch (error) {
      console.error('Error adding staff:', error);
      window.alert('❌ Error adding staff: ' + error.message);
    }
  };

  const filteredStaff = staff.filter(member => {
    const matchesSearch = 
      (member.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (member.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (member.designation || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterRole === 'all' || member.role === filterRole;
    
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading staff...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header & Actions */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold text-gray-900">Staff Management</h3>
          <p className="text-sm text-gray-600 mt-1">
            Total: {staff.length} staff members
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={loadStaff}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
          <button
            onClick={() => setAddingStaff(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            <Plus className="w-4 h-4" />
            <span>Add Staff</span>
          </button>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="grid grid-cols-2 gap-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by name, email, or designation..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none"
          >
            <option value="all">All Roles</option>
            <option value="hod">HOD</option>
            <option value="mentoring_coordinator">Mentoring Coordinator</option>
            <option value="class_coordinator">Class Coordinator</option>
            <option value="mentor">Mentor</option>
          </select>
        </div>
      </div>

      {/* Staff Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Staff Member
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Username
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Designation
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Department
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredStaff.map((member) => (
                <tr key={member.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-purple-100 rounded-full flex items-center justify-center">
                        <span className="text-purple-600 font-semibold">
                          {(member.name || 'U').charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {member.name}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {member.email || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {member.username || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {member.designation || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {member.department || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                      {member.role || 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {member.phone || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleEditStaff(member)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                      title="Edit Staff"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteStaff(member.id, member.name)}
                      className="text-red-600 hover:text-red-900"
                      title="Delete Staff"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredStaff.length === 0 && (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">No staff members found</p>
          </div>
        )}
      </div>

      {/* Edit Staff Modal */}
      {editingStaff && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 my-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Edit Staff Member</h3>
              <button onClick={() => setEditingStaff(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    value={editingStaff.name || ''}
                    onChange={(e) => setEditingStaff({ ...editingStaff, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Designation</label>
                  <input
                    type="text"
                    value={editingStaff.designation || ''}
                    onChange={(e) => setEditingStaff({ ...editingStaff, designation: e.target.value })}
                    placeholder="e.g., Associate Professor"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                <input
                  type="text"
                  value={editingStaff.department || ''}
                  onChange={(e) => setEditingStaff({ ...editingStaff, department: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Qualifications</label>
                <input
                  type="text"
                  value={editingStaff.qualifications || ''}
                  onChange={(e) => setEditingStaff({ ...editingStaff, qualifications: e.target.value })}
                  placeholder="e.g., M.Tech, Ph.D."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                  <input
                    type="tel"
                    value={editingStaff.phone || ''}
                    onChange={(e) => setEditingStaff({ ...editingStaff, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    value={editingStaff.email || ''}
                    onChange={(e) => setEditingStaff({ ...editingStaff, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Username *</label>
                <input
                  type="text"
                  value={editingStaff.username || ''}
                  onChange={(e) => setEditingStaff({ ...editingStaff, username: e.target.value })}
                  placeholder="Login username"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password (leave blank to keep current)</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={editingStaff.password || ''}
                    onChange={(e) => setEditingStaff({ ...editingStaff, password: e.target.value })}
                    placeholder="Enter new password to change"
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">System Role *</label>
                <select
                  value={editingStaff.role || ''}
                  onChange={(e) => setEditingStaff({ ...editingStaff, role: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="hod">HOD</option>
                  <option value="mentoring_coordinator">Mentoring Coordinator</option>
                  <option value="class_coordinator">Class Coordinator</option>
                  <option value="mentor">Mentor</option>
                </select>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 mt-6">
              <button
                onClick={handleSaveStaff}
                className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                <Save className="w-4 h-4" />
                <span>Save Changes</span>
              </button>
              <button
                onClick={() => setEditingStaff(null)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Staff Modal */}
      {addingStaff && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 my-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Add New Staff Member</h3>
              <button onClick={() => setAddingStaff(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    value={newStaff.name}
                    onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Designation</label>
                  <input
                    type="text"
                    value={newStaff.designation}
                    onChange={(e) => setNewStaff({ ...newStaff, designation: e.target.value })}
                    placeholder="e.g., Associate Professor"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                <input
                  type="text"
                  value={newStaff.department}
                  onChange={(e) => setNewStaff({ ...newStaff, department: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Qualifications</label>
                <input
                  type="text"
                  value={newStaff.qualifications}
                  onChange={(e) => setNewStaff({ ...newStaff, qualifications: e.target.value })}
                  placeholder="e.g., M.Tech, Ph.D."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                  <input
                    type="tel"
                    value={newStaff.phone}
                    onChange={(e) => setNewStaff({ ...newStaff, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    value={newStaff.email}
                    onChange={(e) => setNewStaff({ ...newStaff, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Username *</label>
                <input
                  type="text"
                  value={newStaff.username}
                  onChange={(e) => setNewStaff({ ...newStaff, username: e.target.value })}
                  placeholder="Login username"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={newStaff.password}
                    onChange={(e) => setNewStaff({ ...newStaff, password: e.target.value })}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">System Role *</label>
                <select
                  value={newStaff.role}
                  onChange={(e) => setNewStaff({ ...newStaff, role: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="hod">HOD</option>
                  <option value="mentoring_coordinator">Mentoring Coordinator</option>
                  <option value="class_coordinator">Class Coordinator</option>
                  <option value="mentor">Mentor</option>
                </select>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 mt-6">
              <button
                onClick={handleAddStaff}
                className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                <Plus className="w-4 h-4" />
                <span>Add Staff Member</span>
              </button>
              <button
                onClick={() => setAddingStaff(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// System Configuration Component
const SystemConfig = () => {
  const [config, setConfig] = useState({
    institutionName: 'YBIT',
    academicYear: '2024-25',
    enableNotifications: true,
    enableEmailAlerts: true,
    attendanceThreshold: 75,
    lowPerformanceThreshold: 40,
    enableAutoBackup: true,
    backupFrequency: 'daily',
    maxStudentsPerMentor: 20,
    enablePublicRegistration: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, 'systemConfig', 'settings');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setConfig({ ...config, ...docSnap.data() });
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading config:', error);
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const docRef = doc(db, 'systemConfig', 'settings');
      await setDoc(docRef, config);
      window.alert('✅ System configuration saved successfully!');
      setSaving(false);
    } catch (error) {
      console.error('Error saving config:', error);
      window.alert('❌ Error saving configuration: ' + error.message);
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold text-gray-900">System Configuration</h3>
          <p className="text-sm text-gray-600 mt-1">Configure system-wide settings</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400"
        >
          <Save className="w-4 h-4" />
          <span>{saving ? 'Saving...' : 'Save Configuration'}</span>
        </button>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* General Settings */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
            <Settings className="w-5 h-5 mr-2 text-purple-600" />
            General Settings
          </h4>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Institution Name
              </label>
              <input
                type="text"
                value={config.institutionName}
                onChange={(e) => setConfig({ ...config, institutionName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Academic Year
              </label>
              <input
                type="text"
                value={config.academicYear}
                onChange={(e) => setConfig({ ...config, academicYear: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Students Per Mentor
              </label>
              <input
                type="number"
                value={config.maxStudentsPerMentor}
                onChange={(e) => setConfig({ ...config, maxStudentsPerMentor: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>
        </div>

        {/* Thresholds */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
            <Activity className="w-5 h-5 mr-2 text-orange-600" />
            Alert Thresholds
          </h4>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Attendance Threshold (%)
              </label>
              <input
                type="number"
                value={config.attendanceThreshold}
                onChange={(e) => setConfig({ ...config, attendanceThreshold: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
              <p className="text-xs text-gray-500 mt-1">Alert when attendance falls below this value</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Low Performance Threshold (%)
              </label>
              <input
                type="number"
                value={config.lowPerformanceThreshold}
                onChange={(e) => setConfig({ ...config, lowPerformanceThreshold: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
              <p className="text-xs text-gray-500 mt-1">Alert when marks fall below this value</p>
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
            <FileText className="w-5 h-5 mr-2 text-blue-600" />
            Notifications
          </h4>
          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={config.enableNotifications}
                onChange={(e) => setConfig({ ...config, enableNotifications: e.target.checked })}
                className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <span className="ml-2 text-sm text-gray-700">Enable In-App Notifications</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={config.enableEmailAlerts}
                onChange={(e) => setConfig({ ...config, enableEmailAlerts: e.target.checked })}
                className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <span className="ml-2 text-sm text-gray-700">Enable Email Alerts</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={config.enablePublicRegistration}
                onChange={(e) => setConfig({ ...config, enablePublicRegistration: e.target.checked })}
                className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <span className="ml-2 text-sm text-gray-700">Allow Public Registration</span>
            </label>
          </div>
        </div>

        {/* Backup Settings */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
            <Database className="w-5 h-5 mr-2 text-green-600" />
            Backup Settings
          </h4>
          <div className="space-y-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={config.enableAutoBackup}
                onChange={(e) => setConfig({ ...config, enableAutoBackup: e.target.checked })}
                className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <span className="ml-2 text-sm text-gray-700">Enable Automatic Backups</span>
            </label>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Backup Frequency
              </label>
              <select
                value={config.backupFrequency}
                onChange={(e) => setConfig({ ...config, backupFrequency: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                disabled={!config.enableAutoBackup}
              >
                <option value="hourly">Hourly</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Database Backup Component
const DatabaseBackup = () => {
  const [backing, setBacking] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [backups, setBackups] = useState([
    { id: '1', date: new Date().toISOString(), size: '2.4 MB', collections: 15 },
    { id: '2', date: new Date(Date.now() - 86400000).toISOString(), size: '2.3 MB', collections: 15 },
    { id: '3', date: new Date(Date.now() - 172800000).toISOString(), size: '2.2 MB', collections: 15 },
  ]);

  const handleBackup = async () => {
    setBacking(true);
    
    try {
      const collections = ['users', 'students', 'attendance', 'achievements', 'mentorships', 'dashboardConfigs', 'customWidgets'];
      const backup = {};
      
      for (const collectionName of collections) {
        const snap = await getDocs(collection(db, collectionName));
        backup[collectionName] = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      }
      
      const backupData = {
        timestamp: new Date().toISOString(),
        version: '1.0',
        data: backup
      };
      
      const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ybit-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      window.alert('✅ Database backup created successfully!');
    } catch (error) {
      console.error('Error creating backup:', error);
      window.alert('❌ Error creating backup: ' + error.message);
    }
    
    setBacking(false);
  };

  const handleRestore = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!window.confirm('Restore database from backup?\n\nThis will overwrite existing data!')) {
      event.target.value = '';
      return;
    }
    
    setRestoring(true);
    
    try {
      const text = await file.text();
      const backupData = JSON.parse(text);
      
      // Restore collections
      for (const [collectionName, documents] of Object.entries(backupData.data)) {
        for (const document of documents) {
          const docId = document.id;
          delete document.id;
          await setDoc(doc(db, collectionName, docId), document);
        }
      }
      
      window.alert('✅ Database restored successfully!');
      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      console.error('Error restoring backup:', error);
      window.alert('❌ Error restoring backup: ' + error.message);
    }
    
    setRestoring(false);
    event.target.value = '';
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-bold text-gray-900">Database Backup & Restore</h3>
        <p className="text-sm text-gray-600 mt-1">Create and manage database backups</p>
      </div>

      {/* Actions */}
      <div className="grid grid-cols-2 gap-6">
        <div className="bg-gradient-to-br from-green-50 to-emerald-100 border border-green-200 rounded-lg p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="bg-green-500 rounded-full p-3">
              <Download className="w-6 h-6 text-white" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Create Backup</h4>
              <p className="text-sm text-gray-600">Export all database collections</p>
            </div>
          </div>
          <button
            onClick={handleBackup}
            disabled={backing}
            className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 font-medium"
          >
            <Download className="w-5 h-5" />
            <span>{backing ? 'Creating Backup...' : 'Download Backup'}</span>
          </button>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-indigo-100 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="bg-blue-500 rounded-full p-3">
              <Database className="w-6 h-6 text-white" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Restore Backup</h4>
              <p className="text-sm text-gray-600">Import backup file</p>
            </div>
          </div>
          <label className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer font-medium">
            <input
              type="file"
              accept=".json"
              onChange={handleRestore}
              disabled={restoring}
              className="hidden"
            />
            <Database className="w-5 h-5" />
            <span>{restoring ? 'Restoring...' : 'Upload Backup'}</span>
          </label>
        </div>
      </div>

      {/* Backup History */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h4 className="font-semibold text-gray-900 mb-4">Recent Backups</h4>
        <div className="space-y-3">
          {backups.map((backup) => (
            <div key={backup.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center space-x-3">
                <Calendar className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {new Date(backup.date).toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500">
                    {backup.collections} collections • {backup.size}
                  </p>
                </div>
              </div>
              <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                Download
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Warning */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <span className="text-2xl">⚠️</span>
          </div>
          <div className="ml-3">
            <h4 className="text-sm font-semibold text-yellow-800">Important Notes</h4>
            <ul className="mt-2 text-sm text-yellow-700 space-y-1">
              <li>• Backups are exported as JSON files</li>
              <li>• Restoring a backup will overwrite all existing data</li>
              <li>• Always verify backup integrity before restoring</li>
              <li>• Keep backups in a secure location</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// System Logs Component
const SystemLogs = () => {
  const [logs, setLogs] = useState([
    { id: '1', timestamp: new Date(), type: 'info', user: 'admin@ybit.in', action: 'User login', details: 'Successful login' },
    { id: '2', timestamp: new Date(Date.now() - 300000), type: 'success', user: 'mentor@ybit.in', action: 'Achievement added', details: 'Added achievement for student RN001' },
    { id: '3', timestamp: new Date(Date.now() - 600000), type: 'warning', user: 'system', action: 'Low attendance alert', details: 'Student RN002 attendance below 75%' },
    { id: '4', timestamp: new Date(Date.now() - 900000), type: 'error', user: 'system', action: 'Backup failed', details: 'Automatic backup encountered an error' },
    { id: '5', timestamp: new Date(Date.now() - 1200000), type: 'info', user: 'hod@ybit.in', action: 'Dashboard config updated', details: 'Updated widget layout for mentors' },
  ]);
  const [filterType, setFilterType] = useState('all');

  const filteredLogs = filterType === 'all' ? logs : logs.filter(log => log.type === filterType);

  const getLogIcon = (type) => {
    switch(type) {
      case 'success': return <span className="text-green-600">✓</span>;
      case 'error': return <span className="text-red-600">✗</span>;
      case 'warning': return <span className="text-yellow-600">⚠</span>;
      default: return <span className="text-blue-600">ℹ</span>;
    }
  };

  const getLogColor = (type) => {
    switch(type) {
      case 'success': return 'bg-green-50 border-green-200';
      case 'error': return 'bg-red-50 border-red-200';
      case 'warning': return 'bg-yellow-50 border-yellow-200';
      default: return 'bg-blue-50 border-blue-200';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold text-gray-900">System Logs</h3>
          <p className="text-sm text-gray-600 mt-1">View system activity and events</p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
          >
            <option value="all">All Logs</option>
            <option value="info">Info</option>
            <option value="success">Success</option>
            <option value="warning">Warning</option>
            <option value="error">Error</option>
          </select>
          <button className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
            <Download className="w-4 h-4" />
            <span>Export Logs</span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200">
        <div className="divide-y divide-gray-200">
          {filteredLogs.map((log) => (
            <div key={log.id} className={`p-4 ${getLogColor(log.type)}`}>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-1">
                  {getLogIcon(log.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-900">{log.action}</p>
                    <span className="text-xs text-gray-500">
                      {log.timestamp.toLocaleTimeString()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{log.details}</p>
                  <div className="flex items-center space-x-4 mt-2">
                    <span className="text-xs text-gray-500">
                      <strong>User:</strong> {log.user}
                    </span>
                    <span className="text-xs text-gray-500">
                      <strong>Type:</strong> {log.type}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const StatusPill = ({ status = 'neutral', children }) => {
  const classes = {
    success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    warning: 'bg-amber-50 text-amber-700 border-amber-200',
    danger: 'bg-rose-50 text-rose-700 border-rose-200',
    info: 'bg-sky-50 text-sky-700 border-sky-200',
    neutral: 'bg-slate-50 text-slate-700 border-slate-200'
  };

  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold ${classes[status] || classes.neutral}`}>
      {children}
    </span>
  );
};

const DeveloperMetricCard = ({ icon: Icon, label, value, note, tone = 'blue' }) => {
  const tones = {
    blue: 'border-sky-200 bg-sky-50 text-sky-700',
    green: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    amber: 'border-amber-200 bg-amber-50 text-amber-700',
    purple: 'border-violet-200 bg-violet-50 text-violet-700'
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">{label}</p>
          <p className="mt-2 text-3xl font-bold text-gray-950">{value}</p>
          {note && <p className="mt-1 text-sm text-gray-500">{note}</p>}
        </div>
        <div className={`rounded-lg border p-3 ${tones[tone] || tones.blue}`}>
          <Icon className="h-6 w-6" />
        </div>
      </div>
    </div>
  );
};

const QuickActionCard = ({ icon: Icon, title, description, onClick, busy, accent = 'blue' }) => {
  const accents = {
    blue: 'border-sky-200 hover:border-sky-400 hover:bg-sky-50 text-sky-700',
    green: 'border-emerald-200 hover:border-emerald-400 hover:bg-emerald-50 text-emerald-700',
    amber: 'border-amber-200 hover:border-amber-400 hover:bg-amber-50 text-amber-700',
    purple: 'border-violet-200 hover:border-violet-400 hover:bg-violet-50 text-violet-700'
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className={`group flex h-full w-full items-start justify-between gap-4 rounded-lg border bg-white p-4 text-left shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60 ${accents[accent] || accents.blue}`}
    >
      <span className="flex gap-3">
        <span className="rounded-lg bg-gray-50 p-2 text-gray-700 transition group-hover:bg-white">
          <Icon className="h-5 w-5" />
        </span>
        <span>
          <span className="block font-semibold text-gray-950">{title}</span>
          <span className="mt-1 block text-sm text-gray-500">{description}</span>
        </span>
      </span>
      <ArrowRight className="mt-1 h-5 w-5 flex-shrink-0 opacity-60 transition group-hover:translate-x-1" />
    </button>
  );
};

const SystemHealthPanel = ({ driveStatus, stats, lastLoadedAt, collectionCounts }) => {
  const latestBackup = readBackupHistory()[0];
  const hasToken = Boolean(getSessionToken());
  const dashboardCount = collectionCounts.dashboardConfigs || 0;
  const driveOk = driveStatus.status === 'connected';

  const checks = [
    {
      label: 'Google Drive database',
      detail: driveStatus.message || 'Drive status not checked yet.',
      ok: driveOk,
      icon: HardDrive
    },
    {
      label: 'Developer session',
      detail: hasToken ? 'Session token is available for Drive actions.' : 'No session token found.',
      ok: hasToken,
      icon: Shield
    },
    {
      label: 'Role layouts',
      detail: `${dashboardCount} dashboard configuration record${dashboardCount === 1 ? '' : 's'} found.`,
      ok: dashboardCount > 0,
      icon: Layout
    },
    {
      label: 'Latest local backup',
      detail: latestBackup ? `${formatDateTime(latestBackup.date)} with ${latestBackup.records || 0} records.` : 'No backup downloaded from this browser yet.',
      ok: Boolean(latestBackup),
      icon: Download
    }
  ];

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-100 pb-4">
        <div>
          <h3 className="text-lg font-bold text-gray-950">System Health</h3>
          <p className="mt-1 text-sm text-gray-500">Real checks from the current session, Drive API, and stored collections.</p>
        </div>
        <StatusPill status={driveOk ? 'success' : 'warning'}>
          {driveOk ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
          {driveOk ? 'Drive ready' : 'Needs attention'}
        </StatusPill>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-2">
        {checks.map((check) => {
          const Icon = check.icon;
          return (
            <div key={check.label} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-start gap-3">
                <div className={`rounded-lg p-2 ${check.ok ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-semibold text-gray-950">{check.label}</p>
                  <p className="mt-1 text-sm text-gray-600">{check.detail}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex flex-wrap gap-3 text-sm text-gray-600">
        <span className="inline-flex items-center gap-2">
          <Gauge className="h-4 w-4 text-gray-400" />
          {stats.totalRecords || 0} records tracked
        </span>
        <span className="inline-flex items-center gap-2">
          <Clock3 className="h-4 w-4 text-gray-400" />
          Last checked {formatDateTime(lastLoadedAt)}
        </span>
      </div>
    </div>
  );
};

const DataFootprintPanel = ({ collectionCounts }) => {
  const rows = Object.entries(collectionCounts || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10);

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between gap-4 border-b border-gray-100 pb-4">
        <div>
          <h3 className="text-lg font-bold text-gray-950">Data Footprint</h3>
          <p className="mt-1 text-sm text-gray-500">Largest active collections in the app database.</p>
        </div>
        <Database className="h-5 w-5 text-gray-400" />
      </div>

      <div className="mt-4 space-y-3">
        {rows.length === 0 ? (
          <p className="rounded-lg border border-dashed border-gray-200 p-4 text-sm text-gray-500">No collection data loaded yet.</p>
        ) : rows.map(([name, count]) => (
          <div key={name} className="flex items-center justify-between gap-4">
            <span className="truncate text-sm font-medium text-gray-700">{name}</span>
            <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-bold text-gray-700">{count}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const DeveloperBackupPanel = ({ onBackupCreated, onRestored }) => {
  const [backing, setBacking] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [backups, setBackups] = useState(readBackupHistory());
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleBackup = async () => {
    setBacking(true);
    setMessage('');
    setError('');

    try {
      const exported = await buildControlCenterBackup();
      const timestamp = new Date().toISOString();
      const recordCount = countRecords(exported.data);
      const backupPackage = {
        app: 'YBIT Mentoring System',
        source: 'Developer Control Center',
        schemaVersion: 2,
        exportedAt: timestamp,
        mode: exported.mode,
        warning: exported.warning,
        recordCount,
        collections: Object.keys(exported.data || {}),
        data: exported.data || {}
      };
      const filename = `ybit-control-center-${safeFilePart(exported.mode)}-${timestamp.slice(0, 10)}.json`;

      downloadJson(filename, backupPackage);
      const history = writeBackupHistory({
        id: timestamp,
        date: timestamp,
        filename,
        mode: exported.mode,
        records: recordCount,
        collections: backupPackage.collections.length
      });

      setBackups(history);
      setMessage(`Backup downloaded with ${recordCount} records from ${exported.mode}.`);
      onBackupCreated?.(backupPackage);
    } catch (backupError) {
      console.error('Control center backup failed:', backupError);
      setError(backupError.message || 'Could not create backup.');
    } finally {
      setBacking(false);
    }
  };

  const handleRestore = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!window.confirm('Restore this backup to the active site database? This can overwrite existing data.')) {
      event.target.value = '';
      return;
    }

    setRestoring(true);
    setMessage('');
    setError('');

    try {
      const parsed = JSON.parse(await file.text());
      const restoredData = parsed.data || parsed.database || parsed;
      if (!restoredData || typeof restoredData !== 'object' || Array.isArray(restoredData)) {
        throw new Error('Backup file does not contain a valid database object.');
      }

      if ((parsed.mode || parsed.storageMode) === 'collection-export') {
        await restoreCollectionExport(restoredData);
      } else {
        await writeDriveDatabase(restoredData);
      }

      setMessage('Backup restored. Refresh open dashboards before entering new data.');
      onRestored?.();
    } catch (restoreError) {
      console.error('Control center restore failed:', restoreError);
      setError(restoreError.message || 'Could not restore backup.');
    } finally {
      setRestoring(false);
      event.target.value = '';
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="text-xl font-bold text-gray-950">Drive Backup Center</h3>
          <p className="mt-1 text-sm text-gray-600">Download the active Google Drive database and restore trusted backup files.</p>
        </div>
        <StatusPill status="info">
          <HardDrive className="h-3.5 w-3.5" />
          Uses signed-in Drive session
        </StatusPill>
      </div>

      {(message || error) && (
        <div className={`rounded-lg border px-4 py-3 text-sm ${error ? 'border-rose-200 bg-rose-50 text-rose-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
          {error || message}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-emerald-200 bg-white p-5 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-emerald-50 p-3 text-emerald-700">
              <Download className="h-6 w-6" />
            </div>
            <div>
              <h4 className="font-bold text-gray-950">Download Full Backup</h4>
              <p className="mt-1 text-sm text-gray-600">Exports app data, login records, roles, marks, attendance, reports, and Drive photo references.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleBackup}
            disabled={backing}
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-3 font-semibold text-white hover:bg-emerald-700 disabled:bg-gray-400"
          >
            <Download className="h-5 w-5" />
            {backing ? 'Creating backup...' : 'Download backup JSON'}
          </button>
        </div>

        <div className="rounded-lg border border-sky-200 bg-white p-5 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-sky-50 p-3 text-sky-700">
              <Upload className="h-6 w-6" />
            </div>
            <div>
              <h4 className="font-bold text-gray-950">Restore Backup</h4>
              <p className="mt-1 text-sm text-gray-600">Imports a Developer Control Center backup into the active database.</p>
            </div>
          </div>
          <label className="mt-5 flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg bg-sky-600 px-4 py-3 font-semibold text-white hover:bg-sky-700">
            <input
              type="file"
              accept=".json,application/json"
              onChange={handleRestore}
              disabled={restoring}
              className="hidden"
            />
            <Upload className="h-5 w-5" />
            {restoring ? 'Restoring...' : 'Upload backup JSON'}
          </label>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between gap-4 border-b border-gray-100 pb-4">
          <div>
            <h4 className="font-bold text-gray-950">This Browser Backup History</h4>
            <p className="mt-1 text-sm text-gray-500">History is stored locally so the dashboard never pretends to have files it cannot access.</p>
          </div>
          <Clock3 className="h-5 w-5 text-gray-400" />
        </div>

        <div className="mt-4 space-y-3">
          {backups.length === 0 ? (
            <p className="rounded-lg border border-dashed border-gray-200 p-4 text-sm text-gray-500">No backups downloaded from this browser yet.</p>
          ) : backups.map((backup) => (
            <div key={backup.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-gray-50 px-4 py-3">
              <div>
                <p className="text-sm font-semibold text-gray-900">{backup.filename || 'YBIT backup'}</p>
                <p className="text-xs text-gray-500">{formatDateTime(backup.date)} | {backup.collections || 0} collections | {backup.records || 0} records</p>
              </div>
              <StatusPill status={backup.mode === 'google-drive' ? 'success' : 'warning'}>{backup.mode || 'backup'}</StatusPill>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        <div className="flex gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0" />
          <p>Photo files stay in Google Drive. This backup keeps the database references, so keep the Drive photo folders when archiving a year.</p>
        </div>
      </div>
    </div>
  );
};

const DeveloperActivityLog = ({ stats, driveStatus, lastLoadedAt }) => {
  const localBackups = readBackupHistory();
  const logs = [
    {
      id: 'drive',
      type: driveStatus.status === 'connected' ? 'success' : 'warning',
      action: 'Drive status check',
      details: driveStatus.message || 'Drive status has not been checked.',
      timestamp: lastLoadedAt
    },
    {
      id: 'records',
      type: 'info',
      action: 'Control center loaded data',
      details: `${stats.totalRecords || 0} records across ${stats.activeCollections || 0} active collections.`,
      timestamp: lastLoadedAt
    },
    {
      id: 'backup',
      type: localBackups[0] ? 'success' : 'warning',
      action: localBackups[0] ? 'Latest backup available' : 'Backup not downloaded yet',
      details: localBackups[0] ? `${localBackups[0].filename} contains ${localBackups[0].records || 0} records.` : 'Use Drive Backup before making risky changes.',
      timestamp: localBackups[0]?.date || lastLoadedAt
    }
  ];

  const iconFor = {
    success: <CheckCircle2 className="h-4 w-4 text-emerald-600" />,
    warning: <AlertTriangle className="h-4 w-4 text-amber-600" />,
    info: <Activity className="h-4 w-4 text-sky-600" />
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-xl font-bold text-gray-950">Session Activity</h3>
        <p className="mt-1 text-sm text-gray-600">A lightweight activity view based on real dashboard checks.</p>
      </div>
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        {logs.map((log, index) => (
          <div key={log.id} className={`flex gap-3 p-4 ${index > 0 ? 'border-t border-gray-100' : ''}`}>
            <div className="mt-1">{iconFor[log.type] || iconFor.info}</div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-semibold text-gray-950">{log.action}</p>
                <span className="text-xs text-gray-500">{formatDateTime(log.timestamp)}</span>
              </div>
              <p className="mt-1 text-sm text-gray-600">{log.details}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const WidgetLibraryPanel = ({ customWidgets, onCreate, onDelete }) => (
  <div className="space-y-5">
    <div className="flex flex-wrap items-center justify-between gap-4">
      <div>
        <h3 className="text-xl font-bold text-gray-950">Widget Library</h3>
        <p className="mt-1 text-sm text-gray-600">Manage custom dashboard widgets already stored in the system.</p>
      </div>
      <button
        type="button"
        onClick={onCreate}
        className="inline-flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700"
      >
        <Code2 className="h-4 w-4" />
        Open studio
      </button>
    </div>

    {customWidgets.length === 0 ? (
      <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 py-12 text-center">
        <Wrench className="mx-auto h-12 w-12 text-gray-400" />
        <p className="mt-4 text-lg font-semibold text-gray-700">No custom widgets yet</p>
        <p className="mt-1 text-sm text-gray-500">Create one in Widget Studio, then assign it through Role Layouts.</p>
      </div>
    ) : (
      <div className="grid gap-3 lg:grid-cols-2">
        {customWidgets.map(widget => (
          <div key={widget.id} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h4 className="font-bold text-gray-950">{widget.name}</h4>
                <p className="mt-1 text-sm text-gray-600">{widget.description || 'No description provided.'}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <StatusPill status="neutral">{widget.size || 'standard'}</StatusPill>
                  <StatusPill status="info">{(widget.availableFor || []).join(', ') || 'unassigned'}</StatusPill>
                </div>
              </div>
              <button
                type="button"
                onClick={() => onDelete(widget.id, widget.name)}
                className="rounded-lg p-2 text-rose-600 hover:bg-rose-50"
                title="Delete widget"
              >
                <Trash2 className="h-5 w-5" />
              </button>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const ControlCenterHome = ({
  stats,
  driveStatus,
  collectionCounts,
  lastLoadedAt,
  onRefresh,
  onBackup,
  backupBusy,
  setSelectedTab,
  openHodDashboard
}) => (
  <div className="space-y-6">
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
      <DeveloperMetricCard icon={Users} label="Staff" value={stats.totalUsers || 0} note={`${stats.configuredRoles || 0} role assignments`} tone="blue" />
      <DeveloperMetricCard icon={Users} label="Students" value={stats.totalStudents || 0} note="Drive-backed records" tone="green" />
      <DeveloperMetricCard icon={Wrench} label="Widgets" value={stats.totalCustomWidgets || 0} note="Custom tools" tone="purple" />
      <DeveloperMetricCard icon={Database} label="Records" value={stats.totalRecords || 0} note={`${stats.activeCollections || 0} active collections`} tone="amber" />
    </div>

    <div className="grid gap-5 xl:grid-cols-[1.4fr_0.8fr]">
      <SystemHealthPanel
        driveStatus={driveStatus}
        stats={stats}
        lastLoadedAt={lastLoadedAt}
        collectionCounts={collectionCounts}
      />
      <DataFootprintPanel collectionCounts={collectionCounts} />
    </div>

    <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 pb-4">
        <div>
          <h3 className="text-lg font-bold text-gray-950">Quick Actions</h3>
          <p className="mt-1 text-sm text-gray-500">Fast paths for the work that usually needs a developer touch.</p>
        </div>
        <StatusPill status="neutral">
          <Server className="h-3.5 w-3.5" />
          Lightweight controls
        </StatusPill>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <QuickActionCard
          icon={Layout}
          title="Role Layouts"
          description="Configure which widgets appear for each dashboard role."
          onClick={() => setSelectedTab('dashboard-config')}
          accent="blue"
        />
        <QuickActionCard
          icon={Code2}
          title="Widget Studio"
          description="Build or edit custom widgets without leaving this center."
          onClick={() => setSelectedTab('widget-maker')}
          accent="purple"
        />
        <QuickActionCard
          icon={Download}
          title="Download Backup"
          description="Export the current Drive database before major changes."
          onClick={onBackup}
          busy={backupBusy}
          accent="green"
        />
        <QuickActionCard
          icon={RefreshCw}
          title="Refresh Health"
          description="Recheck Drive, collections, and dashboard configuration."
          onClick={onRefresh}
          accent="amber"
        />
      </div>

      <div className="mt-3">
        <button
          type="button"
          onClick={openHodDashboard}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
        >
          <Home className="h-4 w-4" />
          Open HOD dashboard
        </button>
      </div>
    </div>
  </div>
);

// Main Admin Dashboard Component
const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalStudents: 0,
    totalCustomWidgets: 0,
    totalRecords: 0,
    configuredRoles: 0,
    activeCollections: 0
  });
  const [customWidgets, setCustomWidgets] = useState([]);
  const [collectionCounts, setCollectionCounts] = useState({});
  const [driveStatus, setDriveStatus] = useState({
    status: 'checking',
    message: 'Checking Google Drive storage...'
  });
  const [lastLoadedAt, setLastLoadedAt] = useState('');
  const [selectedTab, setSelectedTab] = useState('home');
  const [quickBackupBusy, setQuickBackupBusy] = useState(false);
  const [dashboardMessage, setDashboardMessage] = useState('');

  useEffect(() => {
    const adminSession = sessionStorage.getItem('adminSession');
    if (!adminSession) {
      navigate('/');
      return;
    }
    loadAdminData();
  }, [navigate]);

  const loadAdminData = async () => {
    try {
      setLoading(true);

      const driveCheck = fetch(DRIVE_ROOT_URL, { cache: 'no-store' })
        .then(async (response) => {
          const result = await response.json().catch(() => ({}));
          if (!response.ok || result.success === false) {
            throw new Error(result.error || result.message || 'Google Drive check failed.');
          }
          return {
            status: 'connected',
            message: result.message || result.storage || 'Google Drive storage is responding.'
          };
        })
        .catch((error) => ({
          status: 'warning',
          message: error.message || 'Could not confirm Google Drive status.'
        }));

      const countEntries = await Promise.all(CONTROL_COLLECTIONS.map(async (collectionName) => {
        try {
          const snapshot = await getDocs(collection(db, collectionName));
          return [collectionName, snapshot.size, snapshot.docs.map(item => ({ id: item.id, ...item.data() }))];
        } catch (error) {
          console.warn(`Could not load ${collectionName}.`, error);
          return [collectionName, 0, []];
        }
      }));

      const counts = Object.fromEntries(countEntries.map(([name, size]) => [name, size]));
      const dataByCollection = Object.fromEntries(countEntries.map(([name, , docs]) => [name, docs]));
      const totalRecords = Object.values(counts).reduce((total, count) => total + count, 0);
      const roleAssignments = (dataByCollection.users || []).reduce((total, user) => {
        const roles = Array.isArray(user.roles) ? user.roles.length : (user.role ? 1 : 0);
        const assignments = Array.isArray(user.assignments) ? user.assignments.length : 0;
        return total + roles + assignments;
      }, 0);

      const nextDriveStatus = await driveCheck;
      setDriveStatus(nextDriveStatus);
      setCollectionCounts(counts);
      setStats({
        totalUsers: counts.users || 0,
        totalStudents: counts.students || 0,
        totalCustomWidgets: counts.customWidgets || 0,
        totalRecords,
        configuredRoles: roleAssignments,
        activeCollections: Object.values(counts).filter(Boolean).length
      });

      setCustomWidgets(dataByCollection.customWidgets || []);
      setLastLoadedAt(new Date().toISOString());
    } catch (error) {
      console.error('Error loading admin data:', error);
      setDriveStatus({
        status: 'warning',
        message: error.message || 'Could not load control center data.'
      });
    } finally {
      setLoading(false);
    }
  };

  const runQuickBackup = async () => {
    setQuickBackupBusy(true);
    setDashboardMessage('');

    try {
      const exported = await buildControlCenterBackup();
      const timestamp = new Date().toISOString();
      const backupPackage = {
        app: 'YBIT Mentoring System',
        source: 'Developer Control Center',
        schemaVersion: 2,
        exportedAt: timestamp,
        mode: exported.mode,
        warning: exported.warning,
        recordCount: countRecords(exported.data),
        collections: Object.keys(exported.data || {}),
        data: exported.data || {}
      };
      const filename = `ybit-control-center-${safeFilePart(exported.mode)}-${timestamp.slice(0, 10)}.json`;
      downloadJson(filename, backupPackage);
      writeBackupHistory({
        id: timestamp,
        date: timestamp,
        filename,
        mode: exported.mode,
        records: backupPackage.recordCount,
        collections: backupPackage.collections.length
      });
      setDashboardMessage(`Backup downloaded with ${backupPackage.recordCount} records.`);
    } catch (backupError) {
      console.error('Quick backup failed:', backupError);
      setDashboardMessage(backupError.message || 'Could not download backup.');
    } finally {
      setQuickBackupBusy(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminSession');
    navigate('/staff-login');
  };

  const handleDeleteWidget = async (widgetId, widgetName) => {
    if (!window.confirm(`Delete custom widget "${widgetName}"?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api/widget/${widgetId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (data.success) {
        setCustomWidgets(prev => prev.filter(w => w.id !== widgetId));
        window.alert('✅ Widget deleted successfully!');
        setTimeout(() => window.location.reload(), 1000);
      } else {
        window.alert('❌ Failed to delete widget: ' + data.error);
      }
    } catch (error) {
      console.error('Error deleting widget:', error);
      window.alert('❌ Error: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading Developer Control Center...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-5">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-950 flex items-center gap-3">
                <Server className="w-8 h-8 text-sky-700" />
                Developer Control Center
              </h1>
              <p className="text-gray-600 text-sm mt-1">Lightweight tools for dashboard design, Drive storage, backup, and role configuration.</p>
              {dashboardMessage && (
                <p className="mt-2 text-sm font-medium text-sky-700">{dashboardMessage}</p>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <StatusPill status={driveStatus.status === 'connected' ? 'success' : 'warning'}>
                <HardDrive className="h-3.5 w-3.5" />
                {driveStatus.status === 'connected' ? 'Drive connected' : 'Drive check needed'}
              </StatusPill>
              <StatusPill status="neutral">{stats.totalRecords || 0} records</StatusPill>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-950 hover:bg-gray-800 text-white rounded-lg transition-colors"
              >
                <LogOut className="w-5 h-5" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4 mb-6">
          <DeveloperMetricCard icon={Users} label="Staff" value={stats.totalUsers || 0} note={`${stats.configuredRoles || 0} role assignments`} tone="blue" />
          <DeveloperMetricCard icon={Users} label="Students" value={stats.totalStudents || 0} note="Drive-backed student records" tone="green" />
          <DeveloperMetricCard icon={Wrench} label="Widgets" value={stats.totalCustomWidgets || 0} note="Reusable dashboard tools" tone="purple" />
          <DeveloperMetricCard icon={Database} label="Records" value={stats.totalRecords || 0} note={`${stats.activeCollections || 0} active collections`} tone="amber" />
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-2 px-6 overflow-x-auto" aria-label="Tabs">
              <button
                onClick={() => setSelectedTab('home')}
                className={`py-4 px-4 border-b-2 font-medium text-sm flex items-center space-x-2 whitespace-nowrap ${
                  selectedTab === 'home'
                    ? 'border-sky-500 text-sky-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Home className="w-5 h-5" />
                <span>Control Home</span>
              </button>

              <button
                onClick={() => setSelectedTab('dashboard-config')}
                className={`py-4 px-4 border-b-2 font-medium text-sm flex items-center space-x-2 whitespace-nowrap ${
                  selectedTab === 'dashboard-config'
                    ? 'border-sky-500 text-sky-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Layout className="w-5 h-5" />
                <span>Role Layouts</span>
              </button>
              
              <button
                onClick={() => setSelectedTab('manage-users')}
                className={`py-4 px-4 border-b-2 font-medium text-sm flex items-center space-x-2 whitespace-nowrap ${
                  selectedTab === 'manage-users'
                    ? 'border-sky-500 text-sky-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <UserCog className="w-5 h-5" />
                <span>Legacy Users</span>
              </button>

              <button
                onClick={() => setSelectedTab('system-config')}
                className={`py-4 px-4 border-b-2 font-medium text-sm flex items-center space-x-2 whitespace-nowrap ${
                  selectedTab === 'system-config'
                    ? 'border-sky-500 text-sky-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Settings className="w-5 h-5" />
                <span>Settings</span>
              </button>

              <button
                onClick={() => setSelectedTab('database-backup')}
                className={`py-4 px-4 border-b-2 font-medium text-sm flex items-center space-x-2 whitespace-nowrap ${
                  selectedTab === 'database-backup'
                    ? 'border-sky-500 text-sky-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <HardDrive className="w-5 h-5" />
                <span>Drive Backup</span>
              </button>

              <button
                onClick={() => setSelectedTab('system-logs')}
                className={`py-4 px-4 border-b-2 font-medium text-sm flex items-center space-x-2 whitespace-nowrap ${
                  selectedTab === 'system-logs'
                    ? 'border-sky-500 text-sky-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <FileText className="w-5 h-5" />
                <span>Activity</span>
              </button>
              
              <button
                onClick={() => setSelectedTab('widgets')}
                className={`py-4 px-4 border-b-2 font-medium text-sm whitespace-nowrap ${
                  selectedTab === 'widgets'
                    ? 'border-sky-500 text-sky-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Widget Library
              </button>
              
              <button
                onClick={() => setSelectedTab('widget-maker')}
                className={`py-4 px-4 border-b-2 font-medium text-sm whitespace-nowrap ${
                  selectedTab === 'widget-maker'
                    ? 'border-sky-500 text-sky-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Widget Studio
              </button>
            </nav>
          </div>

          <div className="p-6">
            {selectedTab === 'home' && (
              <ControlCenterHome
                stats={stats}
                driveStatus={driveStatus}
                collectionCounts={collectionCounts}
                lastLoadedAt={lastLoadedAt}
                onRefresh={loadAdminData}
                onBackup={runQuickBackup}
                backupBusy={quickBackupBusy}
                setSelectedTab={setSelectedTab}
                openHodDashboard={() => navigate('/hod/dashboard')}
              />
            )}
            {selectedTab === 'dashboard-config' && <DashboardConfigurator />}
            {selectedTab === 'manage-users' && <ManageUsers />}
            {selectedTab === 'system-config' && <SystemConfig />}
            {selectedTab === 'database-backup' && (
              <DeveloperBackupPanel
                onBackupCreated={loadAdminData}
                onRestored={loadAdminData}
              />
            )}
            {selectedTab === 'system-logs' && (
              <DeveloperActivityLog
                stats={stats}
                driveStatus={driveStatus}
                lastLoadedAt={lastLoadedAt}
              />
            )}
            {selectedTab === 'widgets' && (
              <WidgetLibraryPanel
                customWidgets={customWidgets}
                onCreate={() => setSelectedTab('widget-maker')}
                onDelete={handleDeleteWidget}
              />
            )}

            {false && selectedTab === 'widgets' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Custom Widgets</h3>
                  <button
                    onClick={() => setSelectedTab('widget-maker')}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
                  >
                    🔧 Go to Widget Maker
                  </button>
                </div>

                {customWidgets.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <Database className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg font-medium">No custom widgets yet</p>
                    <p className="text-gray-400 text-sm mt-2">Use Widget Maker to create your first widget</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {customWidgets.map(widget => (
                      <div key={widget.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-purple-300">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900">{widget.name}</h4>
                            <p className="text-sm text-gray-600 mt-1">{widget.description || 'No description'}</p>
                            <div className="flex gap-2 mt-2">
                              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">{widget.size}</span>
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">{(widget.availableFor || []).join(', ')}</span>
                            </div>
                          </div>
                          <button
                            onClick={() => handleDeleteWidget(widget.id, widget.name)}
                            className="ml-4 p-2 text-red-600 hover:bg-red-50 rounded-lg"
                            title="Delete Widget"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {selectedTab === 'widget-maker' && (
              <div>
                <div className="mb-4">
                  <h3 className="text-xl font-bold text-gray-950">Widget Studio</h3>
                  <p className="text-sm text-gray-600 mt-1">Create, test, and store lightweight custom dashboard widgets.</p>
                </div>
                <div className="bg-white rounded-lg border border-gray-200">
                  <WidgetMaker />
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
